/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { isArray } from 'lodash';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    Chip,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    alpha,
    IconButton,
    Popover,
    List,
    ListItemButton,
    ListItemText,
    Checkbox,
    Divider,
    Badge,
    Tooltip,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { fetchMonthlyEnquiries, setMonthlyEnquiries } from 'src/redux/slices/analyticsReports';
import { dispatch, useSelector as useStoreSelector } from 'src/redux/store';
import {
    getTablePreferences,
    handleTablePreferences,
    handleTablePreferencesEdit,
    setTablePreferenceLoading,
} from 'src/redux/slices/tablePreferences';
import { getCurrentTableData } from 'src/utils/tablePreferences';
import useGetCurrentUserId from 'src/hooks/useGetCurrentUserId';
import useReportExportFilename from 'src/hooks/useReportExportFilename';
import { useNavigate } from 'react-router-dom';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { Iconify } from 'src/components';
import TreatmentLeadPopup from './TreatmentLeadPopup';
import MetricLineChart from './MetricLineChart';
import { reportDayStart, reportDayEnd } from '../reportMetricSpecs';

const TABLE_NAME = 'Monthly Enquiries Table';

// Status rows — shared lead-stage palette (blue/purple/amber/green/grey).
const STATUS_ROWS = [
    { id: 'NEW', label: 'Enquiries', color: '#3b82f6' },
    { id: 'CONSULTATION_BOOKED', label: 'Consultation Booked', color: '#8b5cf6' },
    { id: 'CONSULTATION_ATTENDED', label: 'Consultation Attended', color: '#f59e0b' },
    { id: 'TREATMENT_STARTED', label: 'Treatment Started', color: '#10b981' },
    { id: 'CLOSED', label: 'Completed Treatment', color: '#64748b' },
];

// Month names for year comparison columns
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Helper function to get value from month data
const getStatusValue = (monthData, statusKey) => {
    if (!monthData?.data) return 0;
    if (statusKey === 'NEW') {
        return monthData.data.NEW?.actual || 0;
    }
    return monthData.data[statusKey]?.actual || monthData.data[statusKey]?.count || 0;
};

// Helper function to get target from month data
const getStatusTarget = (monthData, statusKey) => {
    if (!monthData?.data) return 0;
    return monthData.data[statusKey]?.target || 0;
};

export default function MonthlyEnquiriesTable({
    practiceId,
    dateRange,
    treatments,
}) {
    const navigate = useNavigate();
    const [viewMode, setViewMode] = useState('timeline'); // 'timeline' or 'yearly'
    const [selectedTreatment, setSelectedTreatment] = useState('');
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupData, setPopupData] = useState({
        status: '',
        statusLabel: '',
        startDate: null,
        endDate: null,
        treatmentId: null,
        treatmentName: '',
    });

    // Toolbar popover anchors
    const [columnsAnchor, setColumnsAnchor] = useState(null);
    const [exportAnchor, setExportAnchor] = useState(null);
    const [hiddenStatuses, setHiddenStatuses] = useState([]);
    const [prefsLoaded, setPrefsLoaded] = useState(false);

    const { monthlyEnquiries, treatmentStatusSummary } = useSelector((state) => state.analyticsReports || {});
    const monthlyData = monthlyEnquiries?.data || [];
    const isLoading = monthlyEnquiries?.isLoading || false;
    const statusSummaryData = treatmentStatusSummary?.data || [];

    // Table preferences from Redux
    const userId = useGetCurrentUserId();
    const buildExportName = useReportExportFilename();
    const { tablePreferences: tablePrefsRaw, globalTablePreferences } = useStoreSelector((state) => state.tablePreferences);
    const prefResults = tablePrefsRaw?.results || tablePrefsRaw || [];

    // Load user preferences on mount
    useEffect(() => {
        if (userId) {
            dispatch(getTablePreferences({ practiceUserId: userId }));
        }
    }, [userId]);

    // Save preferences to API (hiddenStatuses)
    const savePreferences = useCallback((newHiddenStatuses) => {
        if (!userId) return;
        dispatch(setTablePreferenceLoading(true));
        const tableData = getCurrentTableData(prefResults, TABLE_NAME);
        const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
        const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : globalData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        const updatedPrefs = { ...currentPreferences, hiddenStatuses: newHiddenStatuses };
        const payload = {
            body: { table_name: TABLE_NAME, preferences: updatedPrefs },
            practiceUserId: userId,
        };
        if (isArray(tableData) && tableData.length > 0) {
            dispatch(handleTablePreferencesEdit({ ...payload, tableId: tableData[0].id }));
        } else {
            dispatch(handleTablePreferences(payload));
        }
    }, [userId, prefResults, globalTablePreferences]);

    // Load saved preferences
    useEffect(() => {
        if (!prefsLoaded) {
            const tableData = getCurrentTableData(prefResults, TABLE_NAME);
            const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
            const currentTableData = isArray(tableData) && tableData.length > 0 ? tableData : globalData;
            const prefs = currentTableData?.[0]?.preferences;
            const savedHidden = prefs?.hiddenStatuses;
            if (isArray(savedHidden) && savedHidden.length > 0) {
                const validHidden = savedHidden.filter((id) => STATUS_ROWS.some((s) => s.id === id));
                setHiddenStatuses(validHidden);
            }
            setPrefsLoaded(true);
        }
    }, [prefResults, globalTablePreferences]);

    const isFilterActive = hiddenStatuses.length > 0;

    // Handle toggling a status row visibility
    const handleToggleStatus = useCallback((statusId) => {
        const updated = hiddenStatuses.includes(statusId)
            ? hiddenStatuses.filter((id) => id !== statusId)
            : [...hiddenStatuses, statusId];
        setHiddenStatuses(updated);
        savePreferences(updated);
    }, [hiddenStatuses, savePreferences]);

    // Visible statuses (filtered by hiddenStatuses)
    const visibleStatuses = useMemo(
        () => STATUS_ROWS.filter((s) => !hiddenStatuses.includes(s.id)),
        [hiddenStatuses]
    );

    // Set default treatment when treatments list changes
    // Auto-select the treatment with the most enquiries (from status summary data)
    useEffect(() => {
        if (treatments?.length > 0 && !selectedTreatment) {
            // Find treatment with most enquiries from status summary
            if (statusSummaryData.length > 0) {
                // Sort by total descending and get the top one
                const topTreatment = [...statusSummaryData].sort((a, b) => (b.total || 0) - (a.total || 0))[0];
                if (topTreatment?.treatmentItemId) {
                    setSelectedTreatment(topTreatment.treatmentItemId);
                    return;
                }
            }
            // Fallback to first treatment if no status summary data
            const treatmentItemId = treatments[0].treatment_item?.id;
            if (treatmentItemId) {
                setSelectedTreatment(treatmentItemId);
            }
        }
    }, [treatments, statusSummaryData]);

    // Fetch data when treatment or date range changes
    const fetchData = useCallback(() => {
        if (!practiceId || !selectedTreatment) {
            return;
        }

        const formattedDateRange = dateRange ? {
            start: reportDayStart(dateRange.startDate),
            end: reportDayEnd(dateRange.endDate),
        } : null;

        dispatch(fetchMonthlyEnquiries(practiceId, selectedTreatment, formattedDateRange));
    }, [practiceId, selectedTreatment, dateRange]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Cleanup on unmount
    useEffect(() => () => {
        dispatch(setMonthlyEnquiries({ data: [], selectedTreatmentId: null }));
    }, []);

    const handleTreatmentChange = (event) => {
        setSelectedTreatment(event.target.value);
    };

    // Handle cell click to open popup
    const handleCellClick = (monthData, status) => {
        const { year, month } = monthData;
        const startDate = moment({ year, month: month - 1, day: 1 });
        const endDate = moment({ year, month: month - 1 }).endOf('month');

        // Find the treatment to get the practice treatment ID for lead filtering
        const selectedTreatmentObj = treatments?.find(t => t.treatment_item?.id === selectedTreatment);
        const treatmentName = selectedTreatmentObj?.treatment_item?.name || '';

        setPopupData({
            status: status.id,
            statusLabel: status.label,
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD'),
            treatmentId: selectedTreatmentObj?.id, // Use the practice treatment ID
            treatmentName,
        });
        setPopupOpen(true);
    };

    const handlePopupClose = () => {
        setPopupOpen(false);
    };

    // Sort months chronologically (oldest to newest for left-to-right timeline)
    const sortedMonths = useMemo(() => {
        if (!monthlyData || !Array.isArray(monthlyData) || monthlyData.length === 0) {
            return [];
        }
        return [...monthlyData].sort((a, b) => {
            const dateA = (a.year || 0) * 100 + (a.month || 0);
            const dateB = (b.year || 0) * 100 + (b.month || 0);
            return dateA - dateB;
        });
    }, [monthlyData]);

    // Calculate totals for each status (always use ALL statuses for correct totals)
    const statusTotals = useMemo(() => {
        const totals = {};
        STATUS_ROWS.forEach((status) => {
            totals[status.id] = sortedMonths.reduce(
                (sum, month) => sum + getStatusValue(month, status.id),
                0
            );
        });
        return totals;
    }, [sortedMonths]);

    // Year comparison data — group months by year for side-by-side comparison
    const yearComparisonData = useMemo(() => {
        if (!sortedMonths || sortedMonths.length === 0) return { years: [], monthRange: [] };

        // Get unique years sorted ascending
        const yearsSet = new Set(sortedMonths.map((m) => m.year));
        const years = [...yearsSet].sort((a, b) => a - b);

        // Get unique months that appear in the data, sorted 1-12
        const monthsSet = new Set(sortedMonths.map((m) => m.month));
        const monthRange = [...monthsSet].sort((a, b) => a - b);

        // Build a lookup: yearlyLookup[year][month] = monthData
        const yearlyLookup = {};
        years.forEach((y) => { yearlyLookup[y] = {}; });
        sortedMonths.forEach((m) => {
            yearlyLookup[m.year][m.month] = m;
        });

        // Calculate totals per year per status
        const yearTotals = {};
        years.forEach((y) => {
            yearTotals[y] = {};
            STATUS_ROWS.forEach((status) => {
                yearTotals[y][status.id] = monthRange.reduce((sum, mo) => {
                    const md = yearlyLookup[y][mo];
                    return sum + (md ? getStatusValue(md, status.id) : 0);
                }, 0);
            });
        });

        return { years, monthRange, yearlyLookup, yearTotals };
    }, [sortedMonths]);

    // Format month for column header (e.g., "Jan 24")
    const formatMonthHeader = (monthData) => {
        if (!monthData?.year || !monthData?.month) return '';
        return moment({ year: monthData.year, month: monthData.month - 1 }).format('MMM YY');
    };

    // Export to CSV
    const handleExportCSV = useCallback(() => {
        setExportAnchor(null);
        if (sortedMonths.length === 0) return;

        let csvContent;

        if (viewMode === 'yearly') {
            const { years, monthRange, yearlyLookup, yearTotals } = yearComparisonData;
            // Header: Status, Year, Jan, Feb, ..., Total
            const headers = ['Status', 'Year', ...monthRange.map((m) => MONTH_NAMES[m - 1]), 'Total'];
            const rows = [];
            visibleStatuses.forEach((status) => {
                years.forEach((year) => {
                    const monthValues = monthRange.map((mo) => {
                        const md = yearlyLookup[year]?.[mo];
                        return md ? getStatusValue(md, status.id) : 0;
                    });
                    const total = yearTotals[year]?.[status.id] || 0;
                    rows.push([status.label, year, ...monthValues, total]);
                });
            });
            csvContent = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
        } else {
            // Timeline mode (original)
            const headers = ['Status', ...sortedMonths.map((m) => formatMonthHeader(m)), 'Total'];
            const rows = visibleStatuses.map((status) => {
                const monthValues = sortedMonths.map((m) => getStatusValue(m, status.id));
                const total = statusTotals[status.id] || 0;
                return [status.label, ...monthValues, total];
            });
            csvContent = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
        }

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = buildExportName(`monthly-enquiries-${viewMode}`, dateRange);
        link.click();
        URL.revokeObjectURL(link.href);
    }, [sortedMonths, visibleStatuses, statusTotals, viewMode, yearComparisonData, buildExportName, dateRange]);

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                overflow: 'hidden',
            }}
        >
            {/* Header with Treatment Dropdown + Toolbar */}
            <Box
                sx={{
                    p: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>
                    Monthly Enquiries
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id="treatment-select-label">Treatment</InputLabel>
                        <Select
                            labelId="treatment-select-label"
                            value={selectedTreatment}
                            label="Treatment"
                            onChange={handleTreatmentChange}
                            sx={{ borderRadius: '8px' }}
                        >
                            {treatments?.map((treatment) => (
                                <MenuItem key={treatment.treatment_item?.id || treatment.id} value={treatment.treatment_item?.id}>
                                    {treatment.treatment_item?.name || treatment.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* View mode toggle */}
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(e, val) => { if (val) setViewMode(val); }}
                        size="small"
                        sx={{
                            '& .MuiToggleButton-root': {
                                textTransform: 'none',
                                fontSize: '12px',
                                fontWeight: 500,
                                px: 1.5,
                                py: 0.5,
                                border: '1px solid #e2e8f0',
                                '&.Mui-selected': {
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    '&:hover': { bgcolor: 'primary.dark' },
                                },
                            },
                        }}
                    >
                        <ToggleButton value="timeline">
                            <Iconify icon="eva:bar-chart-outline" width={14} sx={{ mr: 0.5 }} />
                            Timeline
                        </ToggleButton>
                        <ToggleButton value="yearly">
                            <Iconify icon="eva:layers-outline" width={14} sx={{ mr: 0.5 }} />
                            By Year
                        </ToggleButton>
                        <ToggleButton value="graph">
                            <Iconify icon="eva:trending-up-outline" width={14} sx={{ mr: 0.5 }} />
                            Graph
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {/* Divider between dropdown and toolbar icons */}
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                    {/* Row visibility */}
                    <Tooltip title="Show/hide status rows" arrow>
                        <Badge
                            variant="dot"
                            invisible={!isFilterActive}
                            sx={{
                                '& .MuiBadge-badge': {
                                    bgcolor: '#3b82f6',
                                    width: 8,
                                    height: 8,
                                    minWidth: 8,
                                    top: 4,
                                    right: 4,
                                },
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={(e) => setColumnsAnchor(e.currentTarget)}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    border: '1px solid',
                                    borderColor: (isFilterActive || columnsAnchor) ? 'primary.main' : '#e2e8f0',
                                    borderRadius: '8px',
                                    bgcolor: columnsAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                    '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                                }}
                            >
                                <Iconify icon="eva:options-2-outline" width={16} sx={{ color: (isFilterActive || columnsAnchor) ? 'primary.main' : '#64748b' }} />
                            </IconButton>
                        </Badge>
                    </Tooltip>

                    {/* Export */}
                    <Tooltip title="Export data" arrow>
                        <IconButton
                            size="small"
                            onClick={(e) => setExportAnchor(e.currentTarget)}
                            sx={{
                                width: 32,
                                height: 32,
                                border: '1px solid',
                                borderColor: exportAnchor ? 'primary.main' : '#e2e8f0',
                                borderRadius: '8px',
                                bgcolor: exportAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                            }}
                        >
                            <Iconify icon="eva:download-outline" width={16} sx={{ color: exportAnchor ? 'primary.main' : '#64748b' }} />
                        </IconButton>
                    </Tooltip>

                    {/* Schedule email report */}
                    <Tooltip title="Schedule this report via email" arrow>
                        <IconButton
                            size="small"
                            onClick={() => navigate(PATH_DASHBOARD.analyticsReports.settings)}
                            sx={{
                                width: 32,
                                height: 32,
                                border: '1px solid',
                                borderColor: '#e2e8f0',
                                borderRadius: '8px',
                                '&:hover': { bgcolor: alpha('#7c3aed', 0.08), borderColor: '#7c3aed' },
                            }}
                        >
                            <Iconify icon="mdi:email-fast-outline" width={16} sx={{ color: '#64748b' }} />
                        </IconButton>
                    </Tooltip>

                    {/* Row visibility popover */}
                    <Popover
                        open={Boolean(columnsAnchor)}
                        anchorEl={columnsAnchor}
                        onClose={() => setColumnsAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', width: 260, maxHeight: 400 } } }}
                    >
                        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                Status Rows
                            </Typography>
                        </Box>
                        <List dense sx={{ py: 0.5 }}>
                            {STATUS_ROWS.map((status) => {
                                const isVisible = !hiddenStatuses.includes(status.id);
                                return (
                                    <ListItemButton
                                        key={status.id}
                                        onClick={() => handleToggleStatus(status.id)}
                                        sx={{ py: 0.5, px: 1.5, opacity: isVisible ? 1 : 0.6 }}
                                    >
                                        <Checkbox
                                            size="small"
                                            checked={isVisible}
                                            sx={{ p: 0, mr: 1 }}
                                        />
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                bgcolor: status.color,
                                                mr: 1,
                                                flexShrink: 0,
                                                opacity: isVisible ? 1 : 0.4,
                                            }}
                                        />
                                        <ListItemText
                                            primary={status.label}
                                            primaryTypographyProps={{
                                                fontSize: '13px',
                                                color: isVisible ? '#1e293b' : '#94a3b8',
                                            }}
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Popover>

                    {/* Export popover */}
                    <Popover
                        open={Boolean(exportAnchor)}
                        anchorEl={exportAnchor}
                        onClose={() => setExportAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 200 } } }}
                    >
                        <List dense sx={{ py: 0.5 }}>
                            <ListItemButton onClick={handleExportCSV} sx={{ py: 1, px: 2 }}>
                                <Iconify icon="mdi:file-delimited-outline" width={18} sx={{ color: '#64748b', mr: 1.5 }} />
                                <ListItemText
                                    primary="Export CSV"
                                    primaryTypographyProps={{ fontSize: '13px' }}
                                />
                            </ListItemButton>
                        </List>
                    </Popover>
                </Box>
            </Box>

            {/* Table */}
            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            )}
            {!isLoading && viewMode === 'yearly' && (
                /* ─── Year Comparison Layout ─── */
                <TableContainer sx={{ maxHeight: 500, overflowX: 'auto' }}>
                    <Table stickyHeader size="small" sx={{ minWidth: Math.max(yearComparisonData.monthRange.length * 70 + 250, 400) }}>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '12px',
                                        color: '#64748b',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        bgcolor: '#f8fafc',
                                        borderBottom: '2px solid #e2e8f0',
                                        minWidth: 160,
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 3,
                                    }}
                                >
                                    Status
                                </TableCell>
                                <TableCell
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '12px',
                                        color: '#64748b',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        bgcolor: '#f8fafc',
                                        borderBottom: '2px solid #e2e8f0',
                                        minWidth: 60,
                                        position: 'sticky',
                                        left: 160,
                                        zIndex: 3,
                                    }}
                                >
                                    Year
                                </TableCell>
                                {yearComparisonData.monthRange.map((mo) => (
                                    <TableCell
                                        key={`yc-header-${mo}`}
                                        align="center"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '11px',
                                            color: '#64748b',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.3px',
                                            bgcolor: '#f8fafc',
                                            borderBottom: '2px solid #e2e8f0',
                                            minWidth: 60,
                                            px: 1,
                                        }}
                                    >
                                        {MONTH_NAMES[mo - 1]}
                                    </TableCell>
                                ))}
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '12px',
                                        color: '#1e293b',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        bgcolor: '#f1f5f9',
                                        borderBottom: '2px solid #e2e8f0',
                                        minWidth: 70,
                                        borderLeft: '2px solid #e2e8f0',
                                    }}
                                >
                                    Total
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedMonths.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary">
                                            {selectedTreatment ? 'No data available for this treatment' : 'Select a treatment to view data'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                visibleStatuses.map((status) =>
                                    yearComparisonData.years.map((year, yearIdx) => (
                                        <TableRow
                                            key={`${status.id}-${year}`}
                                            sx={{
                                                bgcolor: alpha(status.color, 0.02),
                                                '&:hover': { bgcolor: alpha(status.color, 0.06) },
                                                ...(yearIdx === yearComparisonData.years.length - 1 && {
                                                    borderBottom: '2px solid #e2e8f0',
                                                }),
                                            }}
                                        >
                                            {/* Status label - show only on first year row */}
                                            {yearIdx === 0 ? (
                                                <TableCell
                                                    rowSpan={yearComparisonData.years.length}
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: '13px',
                                                        color: status.color,
                                                        bgcolor: alpha(status.color, 0.04),
                                                        position: 'sticky',
                                                        left: 0,
                                                        zIndex: 1,
                                                        borderRight: '1px solid #e2e8f0',
                                                        verticalAlign: 'middle',
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: status.color }} />
                                                        {status.label}
                                                    </Box>
                                                </TableCell>
                                            ) : null}
                                            {/* Year label */}
                                            <TableCell
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: '12px',
                                                    color: '#475569',
                                                    bgcolor: alpha(status.color, 0.04),
                                                    position: 'sticky',
                                                    left: 160,
                                                    zIndex: 1,
                                                    borderRight: '1px solid #e2e8f0',
                                                }}
                                            >
                                                {year}
                                            </TableCell>
                                            {/* Month cells */}
                                            {yearComparisonData.monthRange.map((mo) => {
                                                const md = yearComparisonData.yearlyLookup[year]?.[mo];
                                                const value = md ? getStatusValue(md, status.id) : 0;
                                                const target = md ? getStatusTarget(md, status.id) : 0;
                                                const hitTarget = target > 0 && value >= target;
                                                return (
                                                    <TableCell
                                                        key={`yc-${year}-${mo}-${status.id}`}
                                                        align="center"
                                                        onClick={() => value > 0 && md && handleCellClick(md, status)}
                                                        sx={{
                                                            py: 1,
                                                            cursor: value > 0 ? 'pointer' : 'default',
                                                            '&:hover': value > 0 ? { bgcolor: alpha(status.color, 0.08) } : {},
                                                        }}
                                                    >
                                                        {value > 0 ? (
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                                                <Chip
                                                                    label={value}
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: alpha(status.color, 0.12),
                                                                        color: status.color,
                                                                        fontWeight: 600,
                                                                        fontSize: '12px',
                                                                        height: 26,
                                                                        minWidth: 36,
                                                                        cursor: 'pointer',
                                                                        '&:hover': { bgcolor: alpha(status.color, 0.2) },
                                                                    }}
                                                                />
                                                                {target > 0 && (
                                                                    <Typography sx={{
                                                                        fontSize: '10px',
                                                                        fontWeight: 500,
                                                                        color: hitTarget ? '#10b981' : '#ef4444',
                                                                        lineHeight: 1,
                                                                    }}>
                                                                        {hitTarget ? '✓' : ''} /{target}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        ) : (
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                                                <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>-</Typography>
                                                                {target > 0 && (
                                                                    <Typography sx={{
                                                                        fontSize: '10px',
                                                                        fontWeight: 500,
                                                                        color: '#ef4444',
                                                                        lineHeight: 1,
                                                                    }}>
                                                                        /{target}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                );
                                            })}
                                            {/* Year total */}
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    bgcolor: alpha(status.color, 0.08),
                                                    borderLeft: '2px solid #e2e8f0',
                                                    py: 1.5,
                                                }}
                                            >
                                                <Typography sx={{ fontWeight: 700, fontSize: '14px', color: status.color }}>
                                                    {yearComparisonData.yearTotals[year]?.[status.id] || 0}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {!isLoading && viewMode === 'graph' && (
                <Box sx={{ px: 1, pt: 2, pb: 1 }}>
                    <MetricLineChart
                        categories={sortedMonths.map(formatMonthHeader)}
                        series={visibleStatuses.map((s) => ({
                            name: s.label,
                            color: s.color,
                            data: sortedMonths.map((m) => getStatusValue(m, s.id)),
                        }))}
                        type="line"
                        height={380}
                        yAxisTitle="Leads"
                        maxCategories={null}
                    />
                </Box>
            )}
            {!isLoading && viewMode === 'timeline' && (
                /* ─── Timeline Layout (Original) ─── */
                <TableContainer sx={{ maxHeight: 400, overflowX: 'auto' }}>
                    <Table stickyHeader size="small" sx={{ minWidth: Math.max(sortedMonths.length * 70 + 200, 400) }}>
                        <TableHead>
                            <TableRow>
                                {/* Status column header - sticky */}
                                <TableCell
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '12px',
                                        color: '#64748b',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        bgcolor: '#f8fafc',
                                        borderBottom: '2px solid #e2e8f0',
                                        minWidth: 160,
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 3,
                                    }}
                                >
                                    Status
                                </TableCell>
                                {/* Month columns */}
                                {sortedMonths.map((monthData) => (
                                    <TableCell
                                        key={`header-${monthData.year}-${monthData.month}`}
                                        align="center"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '11px',
                                            color: '#64748b',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.3px',
                                            bgcolor: '#f8fafc',
                                            borderBottom: '2px solid #e2e8f0',
                                            minWidth: 60,
                                            px: 1,
                                        }}
                                    >
                                        {formatMonthHeader(monthData)}
                                    </TableCell>
                                ))}
                                {/* Total column header */}
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '12px',
                                        color: '#1e293b',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        bgcolor: '#f1f5f9',
                                        borderBottom: '2px solid #e2e8f0',
                                        minWidth: 70,
                                        borderLeft: '2px solid #e2e8f0',
                                    }}
                                >
                                    Total
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedMonths.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary">
                                            {selectedTreatment ? 'No data available for this treatment' : 'Select a treatment to view data'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                visibleStatuses.map((status) => (
                                    <TableRow
                                        key={status.id}
                                        sx={{
                                            bgcolor: alpha(status.color, 0.02),
                                            '&:hover': {
                                                bgcolor: alpha(status.color, 0.06),
                                            },
                                        }}
                                    >
                                        {/* Status label - sticky first column */}
                                        <TableCell
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '13px',
                                                color: status.color,
                                                bgcolor: alpha(status.color, 0.04),
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 1,
                                                borderRight: '1px solid #e2e8f0',
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        bgcolor: status.color,
                                                    }}
                                                />
                                                {status.label}
                                            </Box>
                                        </TableCell>
                                        {/* Month value cells - clickable */}
                                        {sortedMonths.map((monthData) => {
                                            const value = getStatusValue(monthData, status.id);
                                            const target = getStatusTarget(monthData, status.id);
                                            const hitTarget = target > 0 && value >= target;
                                            return (
                                                <TableCell
                                                    key={`cell-${monthData.year}-${monthData.month}-${status.id}`}
                                                    align="center"
                                                    onClick={() => value > 0 && handleCellClick(monthData, status)}
                                                    sx={{
                                                        py: 1,
                                                        cursor: value > 0 ? 'pointer' : 'default',
                                                        '&:hover': value > 0 ? {
                                                            bgcolor: alpha(status.color, 0.08),
                                                        } : {},
                                                    }}
                                                >
                                                    {value > 0 ? (
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                                            <Chip
                                                                label={value}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: alpha(status.color, 0.12),
                                                                    color: status.color,
                                                                    fontWeight: 600,
                                                                    fontSize: '12px',
                                                                    height: 26,
                                                                    minWidth: 36,
                                                                    cursor: 'pointer',
                                                                    '&:hover': {
                                                                        bgcolor: alpha(status.color, 0.2),
                                                                    },
                                                                }}
                                                            />
                                                            {target > 0 && (
                                                                <Typography sx={{
                                                                    fontSize: '10px',
                                                                    fontWeight: 500,
                                                                    color: hitTarget ? '#10b981' : '#ef4444',
                                                                    lineHeight: 1,
                                                                }}>
                                                                    {hitTarget ? '✓' : ''} /{target}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                                            <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                                                                -
                                                            </Typography>
                                                            {target > 0 && (
                                                                <Typography sx={{
                                                                    fontSize: '10px',
                                                                    fontWeight: 500,
                                                                    color: '#ef4444',
                                                                    lineHeight: 1,
                                                                }}>
                                                                    /{target}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                        {/* Total cell */}
                                        <TableCell
                                            align="center"
                                            sx={{
                                                bgcolor: alpha(status.color, 0.08),
                                                borderLeft: '2px solid #e2e8f0',
                                                py: 1.5,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: '14px',
                                                    color: status.color,
                                                }}
                                            >
                                                {statusTotals[status.id] || 0}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Lead Popup */}
            <TreatmentLeadPopup
                open={popupOpen}
                onClose={handlePopupClose}
                practiceId={practiceId}
                treatmentId={popupData.treatmentId}
                treatmentName={popupData.treatmentName}
                status={popupData.status}
                dateRange={popupData.startDate ? {
                    startDate: popupData.startDate,
                    endDate: popupData.endDate,
                } : null}
            />
        </Box>
    );
}

MonthlyEnquiriesTable.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dateRange: PropTypes.shape({
        startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
        endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    }),
    treatments: PropTypes.array,
};

MonthlyEnquiriesTable.defaultProps = {
    practiceId: null,
    dateRange: null,
    treatments: [],
};
